/**
 * tokens.ts - TripLink's design system, in one file. Every colour,
 * font, and radius used anywhere in the app should trace back to a token
 * here (Section 2.3: "central colour/spacing/typography system", "avoid
 * hard-coded spacing/colours").
 *
 * DESIGN RATIONALE (so the choices aren't just arbitrary):
 * TripLink sits between a public bus and a private taxi - a shared,
 * scheduled, TICKETED product. The whole visual language leans into that:
 * prices, seat counts, and reference codes are set in a monospace face
 * (like a real ticket printer), and the booking-summary/QR-ticket
 * components use a torn-perforation motif (see PriceBreakdown.module.css)
 * so the digital booking still *feels* like tearing off a paper ticket.
 *
 * Palette: a deep pine-teal (route, trust, movement - not the generic
 * "AI cream + terracotta" combination) paired with a warm marigold accent
 * used ONLY for money/savings/primary actions, so "this number is a price"
 * and "this is the button to press" are the two things colour communicates
 * - not decoration.
 */
export const colors = {
  primary: '#145C4D', // "Route Teal" - headers, links, primary navigation
  primaryHover: '#0E4038',
  primaryLight: '#E4EFEC', // tinted backgrounds/badges on primary
  accent: '#F0A202', // "Sunrise Marigold" - prices, savings, primary CTAs only
  accentHover: '#D68F00',
  danger: '#C1432E', // cancellations, errors - muted terracotta, not alarm-red
  success: '#2E7D5B',
  warning: '#B8860B',
  bg: '#F7F5F1', // warm paper background
  surface: '#FFFFFF',
  border: '#E4E0D8',
  text: '#1C2321',
  textMuted: '#5B645F',
  textOnPrimary: '#FFFFFF',
} as const;

export const fonts = {
  display: `'Sora', 'Segoe UI', sans-serif`, // headings, page titles
  body: `'Inter', 'Segoe UI', sans-serif`, // everything else
  mono: `'IBM Plex Mono', 'SFMono-Regular', monospace`, // prices, seat counts, reference codes
};

export const radius = {
  sm: 6,
  md: 10,
  lg: 16,
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

/** Ant Design's ConfigProvider theme object, built from the tokens above - see app/providers.tsx. */
export const antdThemeTokens = {
  colorPrimary: colors.primary,
  colorInfo: colors.primary,
  colorError: colors.danger,
  colorSuccess: colors.success,
  colorWarning: colors.warning,
  colorBgBase: colors.bg,
  colorBgContainer: colors.surface,
  colorText: colors.text,
  colorTextSecondary: colors.textMuted,
  colorBorder: colors.border,
  fontFamily: fonts.body,
  borderRadius: radius.sm,
  wireframe: false,
};
